/**
 * Represents a hair style option for character avatars.
 *
 * Each hair style has a unique identifier and a descriptive name
 * that identifies the specific style to be applied to the character's
 * hair in the avatar rendering system.
 *
 * @property {number} id - Unique identifier for the hair style option
 * @property {string} value - Descriptive name of the hair style
 */
export type HairStyle = {
  id: number;
  value: string;
};

/**
 * Collection of available hair styles for character avatars.
 *
 * This array defines all the standard hair style options that can be selected
 * for character avatars in the system. The styles range from short to long,
 * straight to curly, and include various cultural and era-specific styles,
 * providing a diverse range of options for character customization.
 *
 * The hair style IDs are used by the character rendering engine to determine which
 * hair style to apply, and they correspond to specific animation assets in the
 * character engine.
 *
 * The styles are organized roughly by length and gender presentation, though
 * all styles can be applied to any character regardless of other attributes.
 *
 */
export const hairStyles: HairStyle[] = [
  // Short/Masculine-presenting styles
  { id: 1, value: 'Short' }, // Basic short hair style
  { id: 2, value: 'Bedhead' }, // Messy, just-woke-up style
  { id: 3, value: 'Fro' }, // Full afro style
  { id: 4, value: 'Mushroom' }, // Bowl cut/mushroom shape
  { id: 5, value: 'Angsty' }, // Emo/scene style with side-swept bangs
  { id: 6, value: 'Mohawk' }, // Classic mohawk style
  { id: 7, value: 'High Fade' }, // Fade haircut with longer top
  { id: 8, value: 'Caesar' }, // Short with slight forward fringe
  { id: 9, value: 'Small Fro' }, // Shorter version of the afro
  { id: 10, value: 'Dapper' }, // Clean, professional style
  { id: 11, value: 'Hipster' }, // Modern undercut style
  { id: 12, value: 'Mascot' }, // Fluffy, mascot-like hair
  { id: 13, value: 'Unkempt' }, // Messy, longer style
  { id: 14, value: '90s' }, // 90s-inspired cut

  // Medium to Long/Feminine-presenting styles
  { id: 15, value: 'Bob' }, // Classic bob haircut
  { id: 16, value: 'Straight Bangs' }, // Straight hair with bangs
  { id: 17, value: 'Medium Bangs' }, // Medium length with bangs
  { id: 18, value: 'High Pony' }, // High ponytail style
  { id: 19, value: 'Poof Pony' }, // Ponytail with volume at crown
  { id: 20, value: 'Seattle' }, // Grunge-inspired style
  { id: 21, value: 'Braid' }, // Single braid style
  { id: 22, value: 'Headband' }, // Style with a headband
  { id: 23, value: 'Textured Pixie' }, // Short, textured pixie cut
  { id: 24, value: 'Barrette' }, // Style with barrette accessory
  { id: 25, value: 'Poof Pigs' }, // Pigtails with volume
  { id: 26, value: 'Mermaid' }, // Long, flowing waves
  { id: 27, value: 'Curly Bob' }, // Bob with curly texture
  { id: 28, value: 'Mullet' }, // Business in front, party in back
];
